// AgendarPage — Novo agendamento com IA.
// Operador preenche dados do cliente; sistema pede recomendação ao motor de IA;
// operador escolhe e copia dados pra abrir a OS no Voalle.

import { useState, useEffect } from 'react';
import { tiposOs, cep as cepApi, geocode, agendamento } from '../lib/api.js';
import CandidatoCard from '../components/CandidatoCard.jsx';
import { JANELAS } from '../lib/janelaPreferencia.js';

export default function AgendarPage() {
  // Form
  const [tipos, setTipos] = useState([]);
  const [form, setForm] = useState({
    tipo_os: '',
    cliente_nome: '',
    cliente_telefone: '',
    cliente_cep: '',
    cliente_endereco: '',
    cliente_numero: '',
    cliente_complemento: '',
    cliente_bairro: '',
    cliente_cidade: '',
    cliente_lat: null,
    cliente_lon: null,
    data: new Date(Date.now() + 86400000).toISOString().slice(0, 10),  // amanhã
    janela_preferencia: 'manha_1',
    modo: 'normal',  // 'normal' ou 'agora'
  });

  // Estado de processamento
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [geocodando, setGeocodando] = useState(false);
  const [geocodeOk, setGeocodeOk] = useState(null);  // null | true | false
  const [sugerindo, setSugerindo] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState(null);

  // Carrega tipos de OS
  useEffect(() => {
    tiposOs.list({ ativo_para_agendamento: true })
      .then(r => setTipos(r.data || []))
      .catch(e => setErro('Erro ao carregar tipos: ' + (e.uiMessage || e.message)));
  }, []);

  function setF(k, v) {
    setForm(f => ({ ...f, [k]: v }));
    if (['cliente_endereco', 'cliente_numero', 'cliente_complemento', 'cliente_bairro', 'cliente_cidade'].includes(k)) {
      setGeocodeOk(null);   // invalidar geocoding ao mudar endereço
      setForm(f => ({ ...f, cliente_lat: null, cliente_lon: null }));
    }
  }

  // Monta string "Rua X, 123, complemento" para geocoding/Voalle
  function enderecoCompleto() {
    const partes = [
      form.cliente_endereco,
      form.cliente_numero,
    ].filter(Boolean).join(', ');
    return form.cliente_complemento ? `${partes} (${form.cliente_complemento})` : partes;
  }

  async function buscarCep() {
    const c = String(form.cliente_cep).replace(/\D/g, '');
    if (c.length !== 8) return;
    setBuscandoCep(true);
    try {
      const r = await cepApi.buscar(c);
      const d = r.data;
      setForm(f => ({
        ...f,
        cliente_endereco: d.logradouro || f.cliente_endereco,
        cliente_bairro: d.bairro || f.cliente_bairro,
        cliente_cidade: d.cidade || f.cliente_cidade,
      }));
    } catch (e) {
      // ViaCEP às vezes 404 silencioso — não bloqueia operação
    } finally {
      setBuscandoCep(false);
    }
  }

  async function localizar() {
    if (!form.cliente_endereco) { setErro('Preencha o endereço primeiro'); return; }
    setGeocodando(true);
    setErro(null);
    try {
      // Geocoding usa endereço + número juntos pra precisão
      const r = await geocode.endereco({
        endereco: enderecoCompleto(),
        bairro: form.cliente_bairro,
        cidade: form.cliente_cidade,
        cep: form.cliente_cep,
      });
      const d = r.data;
      if (d && d.lat) {
        setForm(f => ({ ...f, cliente_lat: Number(d.lat), cliente_lon: Number(d.lon) }));
        setGeocodeOk({ lat: d.lat, lon: d.lon, qualidade: d.qualidade, aproximado: d.aproximado });
      } else {
        setGeocodeOk(false);
      }
    } catch (e) {
      setGeocodeOk(false);
      setErro('Falha no geocoding: ' + (e.uiMessage || e.message));
    } finally {
      setGeocodando(false);
    }
  }

  async function pedirRecomendacao() {
    if (!form.tipo_os) { setErro('Escolha o tipo de OS'); return; }
    if (!form.cliente_endereco) { setErro('Preencha o endereço'); return; }

    // Auto-geocode: se ainda não tem coords, tenta resolver antes de prosseguir
    let lat = form.cliente_lat, lon = form.cliente_lon;
    if (!lat) {
      setErro(null);
      setGeocodando(true);
      try {
        const r = await geocode.endereco({
          endereco: enderecoCompleto(),
          bairro: form.cliente_bairro,
          cidade: form.cliente_cidade,
          cep: form.cliente_cep,
        });
        if (r.data?.lat) {
          lat = Number(r.data.lat);
          lon = Number(r.data.lon);
          setForm(f => ({ ...f, cliente_lat: lat, cliente_lon: lon }));
          setGeocodeOk({ lat: r.data.lat, lon: r.data.lon, qualidade: r.data.qualidade, aproximado: r.data.aproximado });
        } else {
          setGeocodeOk(false);
          setErro('Não foi possível localizar o endereço. Confira a rua, número, bairro e cidade.');
          setGeocodando(false);
          return;
        }
      } catch (e) {
        setGeocodeOk(false);
        setErro('Falha no geocoding: ' + (e.uiMessage || e.message));
        setGeocodando(false);
        return;
      } finally {
        setGeocodando(false);
      }
    }

    setSugerindo(true);
    setErro(null);
    try {
      const r = await agendamento.sugerir({
        tipo_os: form.tipo_os,
        cliente_lat: lat,
        cliente_lon: lon,
        cliente_endereco: enderecoCompleto(),
        cliente_bairro: form.cliente_bairro,
        cliente_cidade: form.cliente_cidade,
        cliente_nome: form.cliente_nome,
        data: form.modo === 'agora' ? undefined : form.data,
        janela_preferencia: form.modo === 'agora' ? undefined : form.janela_preferencia,
        modo: form.modo,
      });
      setResultado(r.data);
    } catch (e) {
      setErro('Erro ao pedir recomendação: ' + (e.uiMessage || e.message));
    } finally {
      setSugerindo(false);
    }
  }

  function copiarParaVoalle() {
    if (!resultado?.candidatos?.length) return;
    const melhor = resultado.ia?.melhor_id
      ? resultado.candidatos.find(c => Number(c.tecnico_id) === Number(resultado.ia.melhor_id)) || resultado.candidatos[0]
      : resultado.candidatos[0];
    const slot = melhor.slot_sugerido;
    const horaIni = slot ? new Date(slot.inicio).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '?';
    const horaFim = slot ? new Date(slot.fim).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '?';
    const dataFmt = new Date(form.data + 'T12:00:00').toLocaleDateString('pt-BR');

    const enderecoFull = [
      form.cliente_endereco,
      form.cliente_numero,
      form.cliente_complemento ? `(${form.cliente_complemento})` : null,
    ].filter(Boolean).join(', ');

    const txt = `📋 DADOS PRA ABRIR NO VOALLE
━━━━━━━━━━━━━━━━━━━━━━━━━━

Tipo: ${form.tipo_os}
Cliente: ${form.cliente_nome}
Telefone: ${form.cliente_telefone}
Endereço: ${enderecoFull}
Bairro: ${form.cliente_bairro}
Cidade: ${form.cliente_cidade}
CEP: ${form.cliente_cep}

🗓 AGENDAMENTO
Data: ${dataFmt}
Janela: ${horaIni} - ${horaFim}
Técnico: ${melhor.tecnico_apelido} (${melhor.tecnico_nome})

📊 SCORE IA: ${melhor.score}/100
${resultado.ia?.raciocinio_top ? '💡 ' + resultado.ia.raciocinio_top : ''}
`.trim();
    navigator.clipboard.writeText(txt);
    alert('✓ Copiado pro clipboard! Cole no Voalle.');
  }

  return (
    <div className="stack">
      <div>
        <h1 style={{ margin: 0, fontSize: 22, display: 'flex', alignItems: 'center', gap: 8 }}>
          ✨ Novo agendamento <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 99, background: '#dbeafe', color: '#1e40af', fontWeight: 600 }}>com IA</span>
        </h1>
        <p className="muted" style={{ marginTop: 4, fontSize: 13 }}>
          Preencha os dados do cliente; a IA recomenda o melhor técnico considerando localização, skill, pontualidade e otimização de rota.
        </p>
      </div>

      {/* === PASSO 1: FORM === */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: 14, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            1. Cliente e serviço
          </h3>
          {/* Toggle: agendar normal x atender agora */}
          <div className="modo-toggle">
            <button type="button"
              className={'modo-tab' + (form.modo === 'normal' ? ' ativo' : '')}
              onClick={() => setF('modo', 'normal')}>
              📅 Agendar
            </button>
            <button type="button"
              className={'modo-tab' + (form.modo === 'agora' ? ' ativo' : '')}
              onClick={() => setF('modo', 'agora')}>
              🚨 Atender agora
            </button>
          </div>
        </div>

        {form.modo === 'agora' && (
          <div style={{ marginBottom: 12, padding: '10px 12px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, fontSize: 12, color: '#78350f', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16 }}>⚡</span>
            <div>
              <strong>Modo urgência:</strong> sistema vai sugerir o técnico mais próximo (via GPS) disponível agora.
              Só lista técnicos com GPS recente (&lt;30min) e sem OS em andamento que termine em mais de 1h.
              Janela e data são ignoradas — usa hoje + agora.
            </div>
          </div>
        )}

        <div className="agendar-grid">
          <label>
            <span>Tipo de OS *</span>
            <select value={form.tipo_os} onChange={e => setF('tipo_os', e.target.value)}>
              <option value="">— escolha —</option>
              {tipos.map(t => (
                <option key={t.id} value={t.tipo}>
                  {t.tipo} {t.janela_recomendada_min ? `(${t.janela_recomendada_min}min)` : ''}
                </option>
              ))}
            </select>
          </label>

          {form.modo !== 'agora' && (
            <label>
              <span>Data preferida *</span>
              <input type="date" value={form.data} onChange={e => setF('data', e.target.value)}/>
            </label>
          )}

          <label>
            <span>Nome do cliente *</span>
            <input type="text" value={form.cliente_nome} onChange={e => setF('cliente_nome', e.target.value)} placeholder="Maria Silva"/>
          </label>

          <label>
            <span>Telefone</span>
            <input type="tel" value={form.cliente_telefone} onChange={e => setF('cliente_telefone', e.target.value)} placeholder="(48) 99999-9999"/>
          </label>

          <label>
            <span>CEP</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <input type="text" value={form.cliente_cep}
                onChange={e => setF('cliente_cep', e.target.value)}
                onBlur={buscarCep}
                placeholder="88010-100"
                style={{ flex: 1 }}/>
              {buscandoCep && <span style={{ fontSize: 11, color: '#6b7280', alignSelf: 'center' }}>⏳</span>}
            </div>
          </label>

          {form.modo !== 'agora' && (
            <label>
              <span>Janela de preferência *</span>
              <select value={form.janela_preferencia} onChange={e => setF('janela_preferencia', e.target.value)}>
                {JANELAS.map(j => (
                  <option key={j.id} value={j.id}>{j.icone} {j.label} ({j.range})</option>
                ))}
                <option value="qualquer">⏰ Qualquer horário (jornada toda do técnico)</option>
              </select>
            </label>
          )}

          <label style={{ gridColumn: '1 / -1' }}>
            <span>Endereço (rua) *</span>
            <input type="text" value={form.cliente_endereco}
              onChange={e => setF('cliente_endereco', e.target.value)}
              placeholder="Rua das Flores"/>
          </label>

          <label>
            <span>Número</span>
            <input type="text" value={form.cliente_numero}
              onChange={e => setF('cliente_numero', e.target.value)}
              placeholder="123"/>
          </label>

          <label>
            <span>Complemento</span>
            <input type="text" value={form.cliente_complemento}
              onChange={e => setF('cliente_complemento', e.target.value)}
              placeholder="apto 302, fundos…"/>
          </label>

          <label>
            <span>Bairro</span>
            <input type="text" value={form.cliente_bairro} onChange={e => setF('cliente_bairro', e.target.value)}/>
          </label>

          <label>
            <span>Cidade</span>
            <input type="text" value={form.cliente_cidade} onChange={e => setF('cliente_cidade', e.target.value)}/>
          </label>

          {/* Feedback de geocoding — mensagem visual + botão localizar manual */}
          <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, flexWrap: 'wrap' }}>
            {geocodeOk?.lat && !geocodeOk?.aproximado && (
              <span style={{ color: '#059669', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                ✓ Endereço localizado{geocodeOk.qualidade ? ` (${geocodeOk.qualidade})` : ''}
              </span>
            )}
            {geocodeOk?.lat && geocodeOk?.aproximado && (
              <span style={{ color: '#d97706', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                ⚠ Localização aproximada ({geocodeOk.qualidade}) — confira com o cliente
              </span>
            )}
            {geocodeOk === false && (
              <span style={{ color: '#dc2626' }}>
                ⚠ Não encontrou coordenadas. Confira rua, número, bairro e cidade.
              </span>
            )}
            {!geocodeOk && form.cliente_endereco && (
              <span style={{ color: '#6b7280' }}>
                🎯 Será localizado automaticamente ao pedir recomendação
              </span>
            )}
            {form.cliente_endereco && (
              <button type="button" className="btn small secondary"
                style={{ marginLeft: 'auto' }}
                onClick={localizar} disabled={geocodando}>
                {geocodando ? '⏳ Localizando…' : '🎯 Localizar agora'}
              </button>
            )}
          </div>
        </div>

        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <button className="btn" onClick={pedirRecomendacao}
            disabled={sugerindo || geocodando || !form.tipo_os || !form.cliente_endereco}
            style={{ fontSize: 14, padding: '10px 24px',
                     background: form.modo === 'agora' ? '#dc2626' : undefined }}>
            {geocodando
              ? '🎯 Localizando endereço…'
              : sugerindo
                ? '🧠 Analisando candidatos…'
                : (form.modo === 'agora' ? '🚨 Despachar técnico AGORA' : '✨ Pedir recomendação à IA')}
          </button>
        </div>

        {erro && (
          <div style={{ marginTop: 10, padding: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#7f1d1d', fontSize: 13 }}>
            ⚠ {erro}
          </div>
        )}
      </div>

      {/* === PASSO 2: RECOMENDAÇÃO === */}
      {resultado && (
        <div className="stack">
          {/* Box do raciocínio IA */}
          {resultado.ia && !resultado.ia.erro && (
            <div className="card" style={{ background: 'linear-gradient(135deg, #f0f9ff, #faf5ff)', borderLeft: '4px solid #3b82f6' }}>
              <h3 style={{ marginTop: 0, fontSize: 14, color: '#1e40af', display: 'flex', alignItems: 'center', gap: 6 }}>
                🧠 Raciocínio da IA
              </h3>
              <p style={{ fontSize: 14, lineHeight: 1.5, color: '#1e293b', margin: '8px 0' }}>
                {resultado.ia.raciocinio_top}
              </p>
              {resultado.ia.raciocinio_alternativas && (
                <p style={{ fontSize: 12, lineHeight: 1.5, color: '#475569', margin: '8px 0' }}>
                  <strong>Alternativas:</strong> {resultado.ia.raciocinio_alternativas}
                </p>
              )}
              {resultado.ia.alertas_gerais?.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  {resultado.ia.alertas_gerais.map((a, i) => (
                    <div key={i} style={{ fontSize: 12, color: '#78350f', background: '#fef3c7', padding: '6px 8px', borderRadius: 4, marginTop: 4 }}>
                      ⚠ {a}
                    </div>
                  ))}
                </div>
              )}
              {resultado.ia.otimizacao_rota && (
                <div style={{ marginTop: 10, padding: 10, background: '#ecfdf5', borderRadius: 6, border: '1px solid #6ee7b7' }}>
                  <strong style={{ fontSize: 12, color: '#065f46' }}>💡 Otimização de rota sugerida:</strong>
                  <div style={{ fontSize: 13, color: '#065f46', marginTop: 4 }}>{resultado.ia.otimizacao_rota}</div>
                </div>
              )}
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 10 }}>
                Modelo: {resultado.ia.modelo} · {resultado.ia.tokens?.input_tokens + resultado.ia.tokens?.output_tokens} tokens
              </div>
            </div>
          )}

          {/* Cards dos candidatos */}
          <div>
            <h3 style={{ fontSize: 14, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
              2. Candidatos avaliados · {resultado.estatisticas?.elegiveis} elegíveis de {resultado.estatisticas?.total_avaliados}
            </h3>
            <div className="stack" style={{ gap: 10 }}>
              {resultado.candidatos.map((c, i) => (
                <CandidatoCard key={c.tecnico_id}
                  candidato={c}
                  posicao={i}
                  melhorEscolhaIA={resultado.ia?.melhor_id}
                  expandido={i === 0}/>
              ))}
            </div>
          </div>

          {/* Botão copiar */}
          <div style={{ textAlign: 'center', padding: 16 }}>
            <button className="btn" onClick={copiarParaVoalle}
              style={{ fontSize: 14, padding: '12px 28px' }}>
              📋 Copiar dados pra abrir no Voalle
            </button>
          </div>

          {/* Estatísticas detalhadas */}
          {resultado.estatisticas?.motivos_descarte && Object.keys(resultado.estatisticas.motivos_descarte).length > 0 && (
            <details style={{ fontSize: 12, color: '#6b7280' }}>
              <summary style={{ cursor: 'pointer' }}>
                📊 {resultado.estatisticas.descartados} técnicos descartados — ver motivos
              </summary>
              <ul style={{ marginTop: 8 }}>
                {Object.entries(resultado.estatisticas.motivos_descarte).map(([motivo, n]) => (
                  <li key={motivo}>{n}× {motivo}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
